import React, { useEffect, useState, useCallback, useRef, forwardRef } from "react";
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { formatToIndianRupee } from "../../utility/FormatToIndianRupee";
import { CREDIT_API_URL } from "../../constants";
import OtpVerificationModal from '../../utility/OtpVerificationModal';
import useChatStore from '../../store/useChatStore';
import { fadeInUp, fadeInLeft, fadeInRight, buttonVariants } from '../../utility/framerMotionAnimations';
import { checkCustomerStatus } from '../../services/leadService';
import './EmiCalculator.css';
import useUtmStore from '../../store/useUtmStore';
import getClientInfo from '../../utility/getClientInfo';
ChartJS.register(ArcElement, Tooltip, Legend);

const EmiSection = forwardRef(({ productType }, ref) => {
    const { t } = useTranslation('homepage');
    const navigate = useNavigate();
    const setCustomer = useChatStore((state) => state.setCustomer);
    const utm = useUtmStore(state => state.utm || {});
    // Calculator Section
    const [loanFactorApiResponse, setLoanFactorApiResponse] = useState({
        minLoanAmount: 0,
        maxLoanAmount: 0,
        minROI: 0,
        maxROI: 0,
        minTenure: 0,
        maxTenure: 0,
    });
    const [loanAmount, setLoanAmount] = useState(0);
    const [interestRate, setInterestRate] = useState(0);
    const [loanTenure, setLoanTenure] = useState(0);
    const [apiResponse, setApiResponse] = useState({
        emi: 0,
        totalPayment: 0,
        totalInterest: 0,
    });
    const [isApiCallInProgress, setIsApiCallInProgress] = useState(false);
    const debounceTimeout = useRef(null);
    const [interestRateText, setInterestRateText] = useState("0%");
    const [loanAmountText, setLoanAmountText] = useState("₹ 0");
    const [loanTenureText, setLoanTenureText] = useState("0 months");

    // Lead creation and OTP verification states
    const [mobileNumber, setMobileNumber] = useState('');
    const [consent, setConsent] = useState(false);
    const [validationErrors, setValidationErrors] = useState({
        mobileNumber: false,
        consent: false
    });
    const [mobileNumberError, setMobileNumberError] = useState('');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [leadData, setLeadData] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Error states for calculator inputs
    const [loanAmountError, setLoanAmountError] = useState('');
    const [interestRateError, setInterestRateError] = useState('');
    const [loanTenureError, setLoanTenureError] = useState('');

    // Editing states
    const [isEditingLoanAmount, setIsEditingLoanAmount] = useState(false);
    const [isEditingInterestRate, setIsEditingInterestRate] = useState(false);
    const [isEditingLoanTenure, setIsEditingLoanTenure] = useState(false);

    // Detect mobile screen
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const updateIsMobile = () => setIsMobile(window.innerWidth <= 768);
        updateIsMobile();
        window.addEventListener('resize', updateIsMobile);
        return () => window.removeEventListener('resize', updateIsMobile);
    }, []);

    // Fetching Loan Factor
    const fetchLoanFactor = useCallback(async () => {
        try {
            if (!productType) return;
            const payload = { productType };
            const response = await axios.post(`${CREDIT_API_URL}fetch-loan-factor`, payload);
            const data = response.data.data;
            setLoanFactorApiResponse(data);
            
            const defaultLoanAmount = 1000000;
            setLoanAmount(defaultLoanAmount);
            setInterestRate(data.minROI);
            setLoanTenure(data.minTenure);
            setLoanAmountText(formatToIndianRupee(defaultLoanAmount));
            setInterestRateText(`${data.minROI}%`);
            setLoanTenureText(`${data.minTenure} months`);
        } catch (error) {
            console.error("Error fetching loan factor:", error.response ? error.response.data : error.message);
        }
    }, [productType]);

    useEffect(() => {
        fetchLoanFactor();
    }, [fetchLoanFactor]);

    // Debounced function to call the loan data API
    const fetchLoanData = useCallback(async (overrides = {}) => {
        if (isApiCallInProgress) return;

        const principal = overrides.principal ?? loanAmount;
        const annualRate = overrides.annualRate ?? interestRate;
        const tenure = overrides.tenure ?? loanTenure;

        setIsApiCallInProgress(true);
        try {
            const payload = {
                principal: principal,
                annualRate: annualRate,
                tenure: tenure,
            };
            const response = await axios.post(`${CREDIT_API_URL}calculate-emi`, payload);
            setApiResponse(response.data.data);
        } catch (error) {
            console.error("Error fetching loan data:", error.response ? error.response.data : error.message);
        } finally {
            setIsApiCallInProgress(false);
        }
    }, [loanAmount, interestRate, loanTenure, isApiCallInProgress]);

    // Debounced state change handlers
    const handleLoanAmountChange = (e) => {
        const newAmount = parseFloat(e.target.value);
        setLoanAmount(newAmount);
        setLoanAmountText(formatToIndianRupee(newAmount));
        setLoanAmountError('');
        debounceApiCall({ principal: newAmount });
    };

    const handleInterestRateChange = (e) => {
        const newRate = parseFloat(e.target.value);
        setInterestRate(newRate);
        setInterestRateText(`${newRate}%`);
        setInterestRateError('');
        debounceApiCall({ annualRate: newRate });
    };

    const handleLoanTenureChange = (e) => {
        const newTenure = Number(e.target.value);
        setLoanTenure(newTenure);
        setLoanTenureText(`${newTenure} months`);
        setLoanTenureError('');
        debounceApiCall({ tenure: newTenure });
    };

    // Debounced function to call the fetchLoanData after delay
    // Accepts an optional `overrides` object so callers (like slider handlers)
    // can pass the latest values directly to the API without relying on state
    // being updated synchronously.
    const debounceApiCall = useCallback((overrides = {}) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            fetchLoanData(overrides);
        }, 500);
    }, [fetchLoanData]);

    // Only trigger loan data fetch once after loanFactorApiResponse is available
    useEffect(() => {
        if (loanFactorApiResponse.minLoanAmount !== undefined) {
            fetchLoanData();
        }
    }, [loanFactorApiResponse]);

    // Validation and utility functions
    const validateMobileNumber = (mobile) => {
        return /^[6-9]\d{9}$/.test(mobile);
    };

    const handleMobileNumberChange = (e) => {
        let value = e.target.value;
        value = value.replace(/[^\d]/g, '');
        if (value.length > 10) {
            value = value.substring(0, 10);
        }
        setMobileNumber(value);
        
        if (value) {
            if (validateMobileNumber(value)) {
                setMobileNumberError('');
                setValidationErrors(prev => ({ ...prev, mobileNumber: false }));
            } else {
                setMobileNumberError('Please enter a valid 10-digit mobile number');
                setValidationErrors(prev => ({ ...prev, mobileNumber: true }));
            }
        } else {
            setMobileNumberError('');
            setValidationErrors(prev => ({ ...prev, mobileNumber: false }));
        }
    };

    const handleConsentChange = (e) => {
        const checked = e.target.checked;
        setConsent(checked);
        if (validationErrors.consent) {
            setValidationErrors(prev => ({ ...prev, consent: false }));
        }
    };

    const handleStartApplication = async () => {
        const errors = {
            mobileNumber: !mobileNumber || !validateMobileNumber(mobileNumber),
            consent: !consent
        };
        setValidationErrors(errors);

        if (!errors.mobileNumber && !errors.consent) {
            setIsProcessing(true);
            try {
                const leadPayload = {
                    name: '',
                    mobile: parseInt(mobileNumber),
                    email: '',
                    productType: productType,
                    loanAmount: loanAmount,
                    businessName: '',
                    isConsentAccepted: consent
                };
                // Attach client/browser info
                try {
                    const clientInfo = getClientInfo();
                    leadPayload.browserName = clientInfo.browserName || null;
                    leadPayload.deviceType = clientInfo.deviceType || null;
                } catch (e) {
                    // ignore detection errors
                }
                // Include UTM tracking params if present
                if (utm) {
                    leadPayload.utmSource = utm.utmSource || null;
                    leadPayload.utmMedium = utm.utmMedium || null;
                    leadPayload.utmCampaign = utm.utmCampaign || "GrowBizh";
                }
                const leadResponse = await axios.post(`${CREDIT_API_URL}create-lead`, leadPayload);
                
                if (leadResponse.status === 200) {
                    const leadDataFromResponse = leadResponse.data.data;
                    setLeadData(leadDataFromResponse);
                    
                    const otpResponse = await axios.post(`${CREDIT_API_URL}generate-otp`, {
                        mobile: parseInt(mobileNumber),
                        productType: productType
                    });
                    
                    if (otpResponse.status === 200) {
                        setShowOtpModal(true);
                    } else {
                        throw new Error('OTP generation failed');
                    }
                } else {
                    throw new Error(leadResponse.data.message || 'Lead creation failed');
                }
            } catch (error) {
                console.error('Error in lead creation:', error);
                // Handle error cases similar to reference code
                if (error.response?.status === 400 || error.response?.status === 409) {
                    if (error.response?.data?.data) {
                        setLeadData(error.response.data.data);
                    }
                    try {
                        const otpResponse = await axios.post(`${CREDIT_API_URL}generate-otp`, {
                            mobile: parseInt(mobileNumber),
                            productType: productType
                        });
                        if (otpResponse.status === 200) {
                            setShowOtpModal(true);
                        }
                    } catch (otpError) {
                        console.error('Error generating OTP:', otpError);
                    }
                }
            } finally {
                setIsProcessing(false);
            }
        }
    };

    const handleOtpSuccess = async () => {
        try {
            if (!leadData) throw new Error("Lead data missing. Please try again.");
            
            await setCustomer(leadData);
            useChatStore.setState({ chatSessionId: leadData.chatSessionId, loanId: leadData.loanRequestId });

            const status = await checkCustomerStatus(leadData.mobile || mobileNumber);
            if (!status.isNewCustomer) {
                window.location.href = `/dashboard`;
                return;
            }
            
            const productTypeLower = (productType || leadData.productType || '').toLowerCase();
            if (productTypeLower.includes("commercial vehicle") || 
                productTypeLower.includes("business loan") ||
                productTypeLower === "business loan" ||
                productTypeLower === "commercial vehicle loan") {
                window.location.href = `/bizzie`;
            } else {
                const msg = document.getElementById("frm-error");
                if (msg) {
                    msg.style.color = "green";
                    msg.textContent = "Thanks!! We will get back to you soon.";
                    msg.style.display = "block";
                }
            }
        } catch (err) {
            console.error("Error in handleOtpSuccess:", err);
            alert(err.message || "OTP verification failed. Please try again.");
        }
    };

    // Check if all required fields are filled
    const isFormValid = () => {
        const isMobileValid = mobileNumber && validateMobileNumber(mobileNumber);
        const hasNoMobileError = !mobileNumberError;
        return isMobileValid && consent && hasNoMobileError;
    };

    const handleLoanAmountInput = (e) => {
        setIsEditingLoanAmount(true);
        const value = e.target.innerText.trim().replace(/[₹,]/g, '').trim();
        const parsedValue = parseInt(value);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (!isNaN(parsedValue)) {
                if (parsedValue >= loanFactorApiResponse.minLoanAmount && parsedValue <= loanFactorApiResponse.maxLoanAmount) {
                    setLoanAmount(parsedValue);
                    setLoanAmountText(formatToIndianRupee(parsedValue));
                    setLoanAmountError('');
                    debounceApiCall({ principal: parsedValue });
                } else {
                    if (parsedValue < loanFactorApiResponse.minLoanAmount) {
                        setLoanAmountError(`Please enter amount between ${formatToIndianRupee(loanFactorApiResponse.minLoanAmount)} and ${formatToIndianRupee(loanFactorApiResponse.maxLoanAmount)}`);
                    } else if (parsedValue > loanFactorApiResponse.maxLoanAmount) {
                        setLoanAmountError(`Please enter amount between ${formatToIndianRupee(loanFactorApiResponse.minLoanAmount)} and ${formatToIndianRupee(loanFactorApiResponse.maxLoanAmount)}`);
                    }
                }
            } else {
                setLoanAmountText(formatToIndianRupee(loanAmount));
                setLoanAmountError('Please enter a valid amount');
            }
        }, 300);
    };

    const handleInterestRateInput = (e) => {
        setIsEditingInterestRate(true);
        let value = e.target.innerText.trim();
        value = value.replace('%', '').trim();
        const parsedValue = parseFloat(value);

        if (!isNaN(parsedValue)) {
            if (parsedValue >= loanFactorApiResponse.minROI && parsedValue <= loanFactorApiResponse.maxROI) {
                setInterestRate(parsedValue);
                setInterestRateText(`${parsedValue}%`);
                setInterestRateError('');
                debounceApiCall({ annualRate: parsedValue });
            } else {
                if (parsedValue < loanFactorApiResponse.minROI) {
                    setInterestRateError(`Please enter interest rate between ${loanFactorApiResponse.minROI}% and ${loanFactorApiResponse.maxROI}%`);
                } else if (parsedValue > loanFactorApiResponse.maxROI) {
                    setInterestRateError(`Please enter interest rate between ${loanFactorApiResponse.minROI}% and ${loanFactorApiResponse.maxROI}%`);
                }
                setInterestRateText(`${interestRate}%`);
            }
        } else {
            setInterestRateText(`${interestRate}%`);
            setInterestRateError('Please enter a valid interest rate');
        }
    };

    const handleLoanTenureInput = (e) => {
        setIsEditingLoanTenure(true);
        let value = e.target.innerText.trim();
        value = value.replace('months', '').trim();
        const parsedValue = parseInt(value);

        if (!isNaN(parsedValue)) {
            if (parsedValue >= loanFactorApiResponse.minTenure && parsedValue <= loanFactorApiResponse.maxTenure) {
                setLoanTenure(parsedValue);
                setLoanTenureText(`${parsedValue} months`);
                setLoanTenureError('');
                debounceApiCall({ tenure: parsedValue });
            } else {
                if (parsedValue < loanFactorApiResponse.minTenure) {
                    setLoanTenureError(`Please enter tenure between ${loanFactorApiResponse.minTenure} and ${loanFactorApiResponse.maxTenure} months`);
                } else if (parsedValue > loanFactorApiResponse.maxTenure) {
                    setLoanTenureError(`Please enter tenure between ${loanFactorApiResponse.minTenure} and ${loanFactorApiResponse.maxTenure} months`);
                }
                setLoanTenureText(`${loanTenure} months`);
            }
        } else {
            setLoanTenureText(`${loanTenure} months`);
            setLoanTenureError('Please enter a valid tenure');
        }
    };

    const interestPayable = apiResponse.totalInterest || 0;

    const data = {
        labels: ["Interest Payable", "Loan Amount"],
        datasets: [
            {
                data: [interestPayable, loanAmount],
                backgroundColor: ['#283782', '#627DD4'],
                borderWidth: 0,
            },
        ],
    };

    const options = {
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
                position: 'nearest',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#ffffff',
                borderWidth: 1,
                padding: 5,
                displayColors: false,
                callbacks: {
                    label: function (tooltipItem) {
                        const value = tooltipItem.raw;
                        return `₹ ${value.toLocaleString('en-IN')}`;
                    },
                },
            },
        },
        cutout: '65%'
    };

    return (
        <div className="emi-section-container" ref={ref}>
            <motion.div 
                className="emi-section-main"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
                variants={fadeInUp}
            >
                <div className="emi-section-bottom">
                    <motion.div 
                        className="emi-section-bottom-left"
                        variants={fadeInLeft}
                    >
                        <div className="emi-section-bottom-left-step">
                            <div className="emi-section-bottom-left-step-top">
                                <label htmlFor="loan-amount">
                                    <span>Required Loan Amount</span>
                                </label>
                                <div
                                    contentEditable
                                    suppressContentEditableWarning
                                    onInput={handleLoanAmountInput}
                                    onFocus={() => setIsEditingLoanAmount(true)}
                                    onBlur={() => setIsEditingLoanAmount(false)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleLoanAmountInput(e);
                                        }
                                    }}
                                    className={`emi-section-bottom-left-step-top-value ${loanAmountError ? 'emi-input-field-error' : ''}`}
                                >
                                    {loanAmountText}
                                </div>
                            </div>
                            <input
                                type="range"
                                id="loan-amount"
                                min={loanFactorApiResponse.minLoanAmount}
                                max={loanFactorApiResponse.maxLoanAmount}
                                step={1000}
                                value={loanAmount}
                                onChange={handleLoanAmountChange}
                                className="emi-range-slider"
                            />
                            <div className="range-slider-container">
                                <label htmlFor="loan-amount" className="range-label min-label">{formatToIndianRupee(loanFactorApiResponse.minLoanAmount)}</label>
                                <label htmlFor="loan-amount" className="range-label max-label">{formatToIndianRupee(loanFactorApiResponse.maxLoanAmount)}</label>
                            </div>
                            {loanAmountError && <div className="emi-error-message">{loanAmountError}</div>}
                        </div>

                        <div className="emi-section-bottom-left-step">
                            <div className="emi-section-bottom-left-step-top">
                                <label htmlFor="loan-interest">
                                    <span>Interest Rate</span>
                                </label>
                                <div
                                    contentEditable
                                    suppressContentEditableWarning
                                    onInput={handleInterestRateInput}
                                    onFocus={() => setIsEditingInterestRate(true)}
                                    onBlur={() => setIsEditingInterestRate(false)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleInterestRateInput(e);
                                        }
                                    }}
                                    className={`emi-section-bottom-left-step-top-value ${interestRateError ? 'emi-input-field-error' : ''}`}
                                >
                                    {interestRateText}
                                </div>
                            </div>
                            <input
                                type="range"
                                id="loan-interest"
                                min={loanFactorApiResponse.minROI}
                                max={loanFactorApiResponse.maxROI}
                                step={0.25}
                                value={interestRate}
                                onChange={handleInterestRateChange}
                                className="emi-range-slider"
                            />
                            <div className="range-slider-container">
                                <label htmlFor="loan-interest" className="range-label min-label">{loanFactorApiResponse.minROI} %</label>
                                <label htmlFor="loan-interest" className="range-label max-label">{loanFactorApiResponse.maxROI} %</label>
                            </div>
                            {interestRateError && <div className="emi-error-message">{interestRateError}</div>}
                        </div>

                        <div className="emi-section-bottom-left-step">
                            <div className="emi-section-bottom-left-step-top">
                                <label htmlFor="loan-tenure">
                                    <span>Loan Tenure</span>
                                </label>
                                <div
                                    contentEditable
                                    suppressContentEditableWarning
                                    onInput={handleLoanTenureInput}
                                    onFocus={() => setIsEditingLoanTenure(true)}
                                    onBlur={() => setIsEditingLoanTenure(false)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleLoanTenureInput(e);
                                        }
                                    }}
                                    className={`emi-section-bottom-left-step-top-value ${loanTenureError ? 'emi-input-field-error' : ''}`}
                                >
                                    {loanTenureText}
                                </div>
                            </div>
                            <input
                                type="range"
                                id="loan-tenure"
                                min={loanFactorApiResponse.minTenure}
                                max={loanFactorApiResponse.maxTenure}
                                value={loanTenure}
                                onChange={handleLoanTenureChange}
                                className="emi-range-slider"
                            />
                            <div className="range-slider-container">
                                <label htmlFor="loan-tenure" className="range-label min-label">{loanFactorApiResponse.minTenure} months</label>
                                <label htmlFor="loan-tenure" className="range-label max-label">{loanFactorApiResponse.maxTenure} months</label>
                            </div>
                            {loanTenureError && <div className="emi-error-message">{loanTenureError}</div>}
                        </div>
                    </motion.div>

                    <motion.div 
                        className="emi-section-bottom-right"
                        variants={fadeInRight}
                    >
                        <div className="emi-section-bottom-right-top">
                            <div className="emi-section-bottom-right-top-chart">
                                <img src="/images/rupee-emi-section.png" alt="" className="emi-chart-icon" />
                                <div style={{ width: '100%', height: '100%' }}>
                                    <Doughnut data={data} options={options} />
                                </div>
                            </div>
                        </div>
                        <div className="emi-section-bottom-right-bottom">
                            <div className="emi-section-bottom-right-bottom-content">
                                <div className="emi-section-label">
                                    <div className="emi-section-label-img"><img src="/images/light-blue-circle.png" alt="" /></div>
                                        <div className="emi-section-label-title">Loan Amount</div>
                                </div>
                                <div className="emi-section-bottom-right-bottom-content-value">{loanAmountText}</div>
                            </div>
                            <div className="emi-section-bottom-right-bottom-content">
                                <div className="emi-section-label">
                                    <div className="emi-section-label-img"><img src="/images/dark-blue-circle.png" alt="" /></div>
                                    <div className="emi-section-label-title">Interest Payable</div>
                                </div>
                                <div className="emi-section-bottom-right-bottom-content-value">{formatToIndianRupee(apiResponse.totalInterest)}</div>
                            </div>
                            <hr className="emi-section-divider" />
                            <div className="emi-section-bottom-right-bottom-content">
                                <div className="emi-section-bottom-right-bottom-content-title">Monthly EMI</div>
                                <div className="emi-section-bottom-right-bottom-content-value emi-bold">{formatToIndianRupee(apiResponse.emi)}</div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                <motion.div 
                    className="emi-form-section-bottom"
                    variants={fadeInUp}
                >
                    <div className="emi-form emi-form-desktop">
                        <div className="emi-form-content">
                            <div className="emi-form-row">
                                <div className="emi-form-col emi-form-col-5">
                                    <div className="emi-homepage-input-field-container">
                                        <div className={`emi-homepage-input-field ${mobileNumberError ? 'emi-homepage-input-field-error' : ''}`}>
                                            <span className="emi-homepage-input-prefix">+91</span>
                                            <input
                                                type="tel"
                                                placeholder=""
                                                value={mobileNumber}
                                                onChange={handleMobileNumberChange}
                                                className="emi-homepage-input-field-text"
                                                maxLength="10"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                            />
                                            <div className="emi-homepage-hero-placeholder">Enter Your Mobile Number</div>
                                        </div>
                                    </div>
                                </div>
                                <motion.div 
                                    className="emi-form-col emi-form-col-4"
                                    variants={buttonVariants}
                                >
                                    <motion.button 
                                        className="emi-homepage-start-application-btn"
                                        onClick={handleStartApplication}
                                        disabled={isProcessing || !isFormValid()}
                                        variants={buttonVariants}
                                        whileHover="hover"
                                        whileTap="tap"
                                    >
                                        {isProcessing ? 'Processing...' : 'Start Application'}
                                    </motion.button>
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    <div className="emi-form emi-form-mobile">
                        <div className="emi-form-mobile-content">
                            <div className="emi-form-row">
                                <div className="emi-form-col emi-form-col-12">
                                    <div className="homepage-input-field-container">
                                        <div className={`homepage-input-field ${validationErrors.mobileNumber ? 'homepage-input-field-error' : ''}`}>
                                            <span className="homepage-input-prefix">+91</span>
                                            <input
                                                type="tel"
                                                placeholder=""
                                                value={mobileNumber}
                                                onChange={handleMobileNumberChange}
                                                className="homepage-input-field-text"
                                                maxLength="10"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                            />
                                            <div className="homepage-hero-placeholder">Enter Your Mobile Number</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="homepage-consent-section emi-form-col emi-form-col-12">
                                    <div className="homepage-consent-content">
                                        <input
                                            type="checkbox"
                                            checked={consent}
                                            onChange={handleConsentChange}
                                            className="homepage-consent-checkbox"
                                        />
                                        <span className="homepage-consent-text">
                                            I allow GrowBizh to send an OTP for my mobile number verification.
                                            I also allow GrowBizh and its loan partner to share me details regarding the loan application process and available offers.
                                        </span>
                                    </div>
                                </div>
                                <motion.div 
                                    className="emi-form-col emi-form-col-6"
                                    variants={buttonVariants}
                                >
                                    <motion.button 
                                        className="emi-homepage-start-application-btn"
                                        onClick={handleStartApplication}
                                        disabled={isProcessing || !isFormValid()}
                                        variants={buttonVariants}
                                        whileHover="hover"
                                        whileTap="tap"
                                    >
                                        {isProcessing ? 'Processing...' : 'Start Application'}
                                    </motion.button>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                <div className="emi-form-consent-section emi-form-consent-desktop">
                    <div className="homepage-consent-section">
                        <div className="homepage-consent-content">
                            <input
                                type="checkbox"
                                checked={consent}
                                onChange={handleConsentChange}
                                className="homepage-consent-checkbox"
                            />
                            <span className="homepage-consent-text">
                                I allow GrowBizh to send an OTP for my mobile number verification.
                                I also allow GrowBizh and its loan partner to share me details regarding the loan application process and available offers.
                            </span>
                        </div>
                    </div>
                </div>
            </motion.div>

            <OtpVerificationModal
                show={showOtpModal}
                mobile={mobileNumber}
                productType={productType}
                onClose={() => setShowOtpModal(false)}
                onVerified={handleOtpSuccess}
            />
        </div>
    );
});

export default EmiSection;

